import * as XLSX from "xlsx";
import * as json2csv from "json2csv";
import { message } from "antd";

export const downloadCSV = (data, filename) => {
  try {
    // Convert JSON data to CSV
    const csv = json2csv.parse(data, {
      withBOM: true, // Ensure BOM (Byte Order Mark) is included for UTF-8 encoding
    });

    // Create a Blob with UTF-8 encoding
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    // Generate a download link and trigger the download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link); // Append to the DOM for triggering
    link.click();
    document.body.removeChild(link); // Clean up after the download
  } catch (error) {
    console.error("Error converting to CSV: ", error);
    message.error("Error while downloading CSV");
  }
};

export const downloadExcel = (data, filename) => {
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet 1");
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

const MAX_XLSX_ROWS = 75000;

let exportState = null;

const truncateRow = (row) => {
  const truncatedRow = {};
  for (const key in row) {
    const cellValue = row[key];
    truncatedRow[key] =
      typeof cellValue === "string" && cellValue.length > 32767
        ? cellValue.slice(0, 32767)
        : cellValue;
  }
  return truncatedRow;
};

const appendXlsxChunk = (chunk) => {
  const truncated = chunk.map(truncateRow);
  if (!exportState.xlsxWorksheet) {
    exportState.xlsxWorksheet = XLSX.utils.json_to_sheet(truncated);
  } else {
    XLSX.utils.sheet_add_json(exportState.xlsxWorksheet, truncated, {
      origin: -1,
      skipHeader: true,
    });
  }
};

const appendCsvChunk = (chunk) => {
  // header only in the very first CSV part written
  const csv = json2csv.parse(chunk, { header: !exportState.headerWritten });
  exportState.parts.push(exportState.headerWritten ? "\n" + csv : csv);
  exportState.headerWritten = true;
};

const triggerBlobDownload = (blob, filename) => {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

// Call once when streaming starts.
// requestedFormat: "Excel" | "CSV"
export const initExportStream = (filename, requestedFormat) => {
  exportState = {
    filename,
    requestedFormat,
    rowCount: 0,
    headerWritten: false,
    parts: [], // CSV mode
    xlsxWorkbook: requestedFormat === "Excel" ? XLSX.utils.book_new() : null,
    xlsxWorksheet: null,
    downgraded: false,
  };
};

// Call for every incoming chunk. `chunk` should already be the
// *formatted* rows for just this chunk (not the whole dataset).
export const appendExportChunk = (chunk) => {
  if (!exportState) {
    console.error("appendExportChunk called before initExportStream");
    return;
  }
  if (!chunk || chunk.length === 0) return;

  if (
    exportState.requestedFormat === "Excel" &&
    !exportState.downgraded &&
    exportState.rowCount + chunk.length > MAX_XLSX_ROWS
  ) {
    exportState.downgraded = true;

    // IMPORTANT: don't just drop the xlsx worksheet — it already holds
    // every row written so far. Pull those rows back out and push them
    // into the CSV parts first, THEN switch to CSV for what's left.
    // Otherwise the download silently starts from wherever the
    // downgrade happened instead of row 1.
    if (exportState.xlsxWorksheet) {
      const existingRows = XLSX.utils.sheet_to_json(
        exportState.xlsxWorksheet,
        { raw: false },
      );
      if (existingRows.length > 0) {
        const csv = json2csv.parse(existingRows, { header: true });
        exportState.parts.push(csv);
        exportState.headerWritten = true;
      }
    }

    exportState.xlsxWorkbook = null;
    exportState.xlsxWorksheet = null;
    message.warning(
      "Large report detected — continuing as CSV for a smooth download.",
    );
  }

  if (exportState.requestedFormat === "CSV" || exportState.downgraded) {
    appendCsvChunk(chunk);
  } else {
    appendXlsxChunk(chunk);
  }
  exportState.rowCount += chunk.length;
};

// Call once streaming has finished.
export const finalizeExportStream = () => {
  if (!exportState) return;
  const useCsv =
    exportState.requestedFormat === "CSV" || exportState.downgraded;

  try {
    if (useCsv) {
      if (exportState.parts.length === 0) {
        console.warn("No data to export");
        return;
      }
      const blob = new Blob(exportState.parts, { type: "text/csv" });
      triggerBlobDownload(blob, `${exportState.filename}.csv`);
    } else {
      if (!exportState.xlsxWorksheet) {
        console.warn("No data to export");
        return;
      }
      XLSX.utils.book_append_sheet(
        exportState.xlsxWorkbook,
        exportState.xlsxWorksheet,
        "Sheet 1",
      );
      const wbout = XLSX.write(exportState.xlsxWorkbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      triggerBlobDownload(blob, `${exportState.filename}.xlsx`);
    }
  } catch (error) {
    console.error("Error finalizing export:", error);
    message.error("Error while generating the report file");
  } finally {
    exportState = null; // free memory
  }
};

export const getExportRowCount = () => exportState?.rowCount || 0;

export const downloadOrUpdateExcel = (newData, filename) => {
  let wb;
  try {
    wb = XLSX.utils.book_new();
  } catch (error) {
    console.error("Error creating workbook:", error);
    return;
  }

  // Check and truncate long text values in newData
  const truncatedData = newData.map((row) => {
    const truncatedRow = {};
    for (const key in row) {
      const cellValue = row[key];
      // console.log("cellValue-----",cellValue)
      truncatedRow[key] =
        typeof cellValue === "string" && cellValue.length > 32767
          ? cellValue.slice(0, 32767) // Truncate to 32,767 characters
          : cellValue;
    }
    return truncatedRow;
  });

  // Convert new (truncated) data to worksheet
  const newWs = XLSX.utils.json_to_sheet(truncatedData);

  // Append or create sheet in workbook
  XLSX.utils.book_append_sheet(wb, newWs, "Sheet 1");

  // Write workbook to a downloadable file
  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], { type: "application/octet-stream" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}.xlsx`;
  link.click();
};


// Amount wise create sheet in excel
// export const downloadOrUpdateExcel = (data, filename, groupByKey) => {
//   try {
//     // Create a new workbook
//     const wb = XLSX.utils.book_new();

//     // Group data based on the specified key (e.g., "amount")
//     const groupedData = data.reduce((acc, row) => {
//       console.log("row-----", row);
//       const groupKey = row[groupByKey] || "Others"; // Use "Others" for undefined or empty values
//       acc[groupKey] = acc[groupKey] || [];
//       acc[groupKey].push(row);
//       return acc;
//     }, {});

//     // Create a sheet for each group
//     Object.entries(groupedData).forEach(([group, rows]) => {
//       // Truncate long text values in each group
//       const truncatedData = rows.map((row) => {
//         const truncatedRow = {};
//         for (const key in row) {
//           const cellValue = row[key];
//           truncatedRow[key] =
//             typeof cellValue === "string" && cellValue.length > 32767
//               ? cellValue.slice(0, 32767) // Truncate to 32,767 characters
//               : cellValue;
//         }
//         return truncatedRow;
//       });

//       // Convert truncated data to worksheet
//       const ws = XLSX.utils.json_to_sheet(truncatedData);

//       // Append the worksheet to the workbook with the group name as the sheet name
//       XLSX.utils.book_append_sheet(wb, ws, group);
//     });

//     // Write the workbook to a downloadable file
//     const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
//     const blob = new Blob([wbout], { type: "application/octet-stream" });
//     const link = document.createElement("a");
//     link.href = URL.createObjectURL(blob);
//     link.download = `${filename}.xlsx`;
//     link.click();
//   } catch (error) {
//     console.error("Error creating Excel file:", error);
//   }
// };

// Amount And Count Show in Excel
// export const downloadOrUpdateExcel = (data, filename) => {
//   try {
//     // Create a new workbook
//     const wb = XLSX.utils.book_new();

//     // Convert data to worksheet
//     const ws = XLSX.utils.json_to_sheet(data);

//     // Append the worksheet to the workbook
//     XLSX.utils.book_append_sheet(wb, ws, "Report");

//     // Write the workbook to a downloadable file
//     const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
//     const blob = new Blob([wbout], { type: "application/octet-stream" });
//     const link = document.createElement("a");
//     link.href = URL.createObjectURL(blob);
//     link.download = `${filename}.xlsx`;
//     link.click();
//   } catch (error) {
//     console.error("Error creating Excel file:", error);
//   }
// };

